import { alpha, Box, ClickAwayListener, InputBase, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import type { Department } from '@/types/department';
import type { Member } from '@/types/user';
import { useState, useMemo } from 'react';
import { Search } from '@mui/icons-material';
function checkIfDepartment(obj: any): obj is Department {
  return obj.sectorList !== undefined 
}

interface ManagerialBlockProps {
  title: string;
  dropdownOptions: Department[] | Member[];
  selectedIds: string[];
  isSubprojectPage: boolean;
  fontSize: string;
}

export const ManagerialBlock: React.FC<ManagerialBlockProps> = ({
  title,
  selectedIds,
  dropdownOptions,
  isSubprojectPage,
  fontSize,
}) => {
  return (
    <>
    <Grid size={4}>
      <Box sx={{ display: 'column', gap: 4 }}>
        <Typography
          variant={isSubprojectPage ? 'h6' : 'body2'}
          color={isSubprojectPage ? 'primary.dark' : 'white'}
          sx={{ fontSize: fontSize, fontWeight: isSubprojectPage ? 600 : 400 }}
        >
          {title}
        </Typography>
        {selectedIds.length > 0 
        ? (selectedIds.map((selectedId) => {
          const item = dropdownOptions.find((o) => o.id === selectedId);
          return item ? (
            <Typography
              key={selectedId}
              variant={isSubprojectPage ? 'h6' : 'body2'}
              color={isSubprojectPage ? 'primary.dark' : 'white'}
              sx={{ fontSize: fontSize, fontWeight: isSubprojectPage ? 500 : 300 }}
            >
              {checkIfDepartment(item)
              ? item.name
              : `${item.userDetails.firstName} ${item.userDetails.lastName}`}
            </Typography>
          ) : null;
        })) : (
            <Typography
              variant={isSubprojectPage ? 'h6' : 'body2'}
              color={isSubprojectPage ? 'primary.dark' : 'white'}
              sx={{ fontSize: fontSize, fontWeight: isSubprojectPage ? 500 : 300 }}
            >
              N/A
            </Typography>
        )}
      </Box>
    </Grid>
    </>
  )
}

interface MultiSelectProps {
  title: string;
  onAdd: (selectedId: string) => void;
  onRemove: (selectedId: string) => void;
  dropdownOptions: Department[] | Member[];
  selectedIds: string[];
  isDepartment: boolean;
}


// TODO: deal with paginated user lists
// Also need to add the 'add user / department' option 
// for if a user doesn't appear on the list
export const MultiSelect: React.FC<MultiSelectProps> = ({
  title,
  onAdd,
  onRemove,
  dropdownOptions,
  selectedIds,
  isDepartment
}) => {
  const theme = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter projects based on selected fiscal year, search query, and filters
  const filteredOptions: Department[] | Member[] = useMemo(() => {
    if (!dropdownOptions) return [];

    let filtered : any[] = dropdownOptions;

    // Filter by if item has been selected already
    filtered = filtered.filter((d) => !selectedIds.includes(d.id))
    
    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (p) =>
          checkIfDepartment(p)
          ? p.name.toLowerCase().includes(searchQuery.toLowerCase())  
          : p.userDetails.firstName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }
    return filtered;
  }, [searchQuery, selectedIds, dropdownOptions]);

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'left',
          justifyContent: 'center',
          minHeight: 'clamp(120px, 15vh, 160px)',
          width: '100%',
          backgroundColor: 'white',
          p: '20px',
          borderRadius: 1
        }}
      >
        <Typography 
          variant={'subtitle2'} 
          sx={{ 
            fontSize: '14px', 
            mb: 'clamp(12px, 1.7vh, 20px)', 
            color: 'background.accent',
          }}
        >
          {title}
        </Typography>
        <ClickAwayListener onClickAway={() => {setDropdownOpen(false); setSearchQuery('');}}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            {/* Input box containing selected tags */}
            <Box
              sx={{
                backgroundColor: 'rgba(14, 70, 63, 0.3)',
                borderBottom: 2,
                borderColor: 'black',
                width: '100%',
                minHeight: 'clamp(35px, 5vh, 55px)',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 'clamp(4px, 0.5vh, 8px)',
                px: 'clamp(6px, 0.6vw, 10px)',
                py: 'clamp(4px, 0.5vh, 8px)',
              }}
            >
              {/* Selected tags inside input box */}
              {selectedIds.map((selectedId) => {
                const item = dropdownOptions.find(
                  (o) => o.id === selectedId,
                );
                return item ? (
                  <Box
                    key={selectedId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: 'info.main',
                      color: 'info.dark',
                      height: 'clamp(22px, 3.5vh, 38px)',
                      px: 'clamp(8px, 0.8vw, 12px)',
                      borderRadius: '4px',
                      minWidth: '80px',
                      maxWidth: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    <Typography 
                        variant={'subtitle2'} 
                        sx={{ 
                          fontSize: '13px', 
                          color: 'black',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                      {checkIfDepartment(item)
                      ? item.name
                      : `${item.userDetails.firstName} ${item.userDetails.lastName}`}
                    </Typography>
                    <Typography 
                        variant={'subtitle2'}
                        onClick={() =>
                          onRemove(selectedId)
                        }
                        sx={{ 
                          fontSize: '14px', 
                          color: 'black',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          ml: 3
                        }}
                      >
                      x
                    </Typography>
                  </Box>
                ) : null;
              })}
            </Box>
            {/* Add button */}
            <Box sx={{ position: 'relative', width: '100%' }}>
              <Box
                onClick={() => {setDropdownOpen(!dropdownOpen); setSearchQuery('');}}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'primary.main',
                  height: 'clamp(30px, 3vh, 40px)',
                  width: '100%',
                  px: 'clamp(8px, 0.8vw, 12px)',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  '&:hover': {
                    backgroundColor: '#0a5c52',
                  },
                }}
              >
                <Typography 
                  variant={'subtitle2'}
                  sx={{ 
                    fontSize: '14px',
                  }}
                >
                  {`Add ${isDepartment ? "Department" : "User"}`}
                </Typography>
              </Box>

              {/* Dropdown menu - matches input box background, same width as button */}
              {dropdownOpen && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    mt: 0,
                    backgroundColor: 'info.main',
                    borderRadius: 0,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    zIndex: 1000,
                    width: '100%',
                    maxHeight: 'clamp(95px, 14vh, 150px)',
                    overflowY: 'auto',
                  }}
                >
                  {dropdownOptions.length > 0 && 
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.info.dark, 0.1),
                      color: 'info.dark',
                      px: 1,
                      minWidth: 'initial',
                      cursor: 'pointer',
                      borderBottom: 1,
                      borderColor: alpha(theme.palette.info.dark, 0.4),
                    }}
                  >
                    <Search sx={{ height: 20, width: 20 }} />
                    <InputBase
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        fontSize: '13px',
                        color: 'info.dark',
                        px: 2
                      }}
                      placeholder='Search...'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </Box>}
                  {filteredOptions.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => {
                          onAdd(item.id);
                      }}
                      sx={{
                        py: 1,
                        cursor: 'pointer',
                        textAlign: 'center',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                        },
                      }}
                    >
                      <Typography 
                        variant={'subtitle2'} 
                        sx={{ 
                          fontSize: '13px', 
                          color: 'black',
                        }}
                      >
                        {checkIfDepartment(item)
                        ? item.name
                        : `${item.userDetails.firstName} ${item.userDetails.lastName}`}
                      </Typography>
                    </Box>
                  ))}
                    <Box
                      key={'addUser'}
                      sx={{
                        py: 1,
                        px: 1,
                        textAlign: 'center',
                      }}
                    >
                      <Typography 
                        variant={'subtitle2'} 
                        sx={{ 
                          fontSize: '13px', 
                          color: 'black',
                          fontWeight: 500,
                          fontStyle: 'italic',
                        }}
                      >
                        - END OF LIST -
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
          </Box>
        </ClickAwayListener>
      </Box>
    </Grid>
  );
}

// might need this later for user look up / pagination
// <Pagination
//   count={Math.ceil((usersQuery.data?.count || 0) / 20)}
//   page={page}
//   onChange={(_, value) => setPage(value)}
//   sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
// />